import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import type { RiskAssessmentResultControlType as Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { RiskScoringModel } from '@risksmart-app/domain/src/types/consts/risk-scoring-model';
import { RiskScoringModel as Risk_Scoring_Model_Enum } from '@risksmart-app/domain/src/types/consts/risk-scoring-model';
import type { FC } from 'react';
import { UNRATED } from 'src/pages/controls/lookupData';
import { useCalculateRiskRating } from 'src/ratings/useCalculateRiskRating';
import { useScoringSettings } from 'src/ratings/useScoringSettings';

import { getRiskScoreBadge } from '@/hooks/useRiskScore';

export type Props = {
  controlType: Risk_Assessment_Result_Control_Type_Enum;
  riskScoreModel: RiskScoringModel;
  impact: null | number;
  likelihood: null | number;
  rating: null | number;
  score: null | number;
  // The config version this score was assessed under; the score-cell colour
  // resolves against it (omitted → latest config).
  configId?: null | string;
  // Config versions to preload. Pass the table's batched set, not just this
  // row's id, so every badge shares one config fetch rather than one per row.
  configIds?: string[];
};

export const RiskScoreBadge: FC<Props> = ({
  controlType,
  impact,
  likelihood,
  rating,
  riskScoreModel,
  score,
  configId,
  configIds,
}) => {
  const { hasScoringSettings, getRatingByLikelihoodAndImpact } =
    useScoringSettings({ configType: 'risk_assessment', configIds });
  const { getByValue } = useRating(
    controlType === 'Controlled' ? 'risk_controlled' : 'risk_uncontrolled'
  );

  const calculateRating = useCalculateRiskRating(controlType);

  if (hasScoringSettings) {
    if (likelihood != null && impact != null) {
      const cell = getRatingByLikelihoodAndImpact(likelihood, impact, configId);
      if (cell) {
        return (
          <SimpleRatingBadge
            rating={{
              label: score != null ? score.toFixed(1) : cell.label,
              color: cell.color,
            }}
          />
        );
      }
    }

    return <SimpleRatingBadge rating={UNRATED} />;
  }

  // Currently the only way to get a score rating category is to use the impact likelihood values.
  // This is because when using the default scoring model,
  // a) the rating can be overriden so it has no reflection on impact likelihood, whilst the score is always likelihod * impact
  // b) a single score value could potentially map to multiple rating categories e.g. a score of 12 = likelihood 3, impact 4, or likelihood 4, impact 3 (which could have different labels, colours)
  // Note we could simplify this in the future by storing a score-rating value on the risk_score table
  const isDefaultScoringModel =
    riskScoreModel === Risk_Scoring_Model_Enum.Default;

  // An unrated risk has no likelihood/impact values. Don't coerce them to 0 —
  // Math.max(0, 1) * Math.max(0, 1) in useCalculateRiskRating maps to the lowest
  // rating band, which made unrated risks render as "Very Low" (RSP-5599).
  const scoreRating = isDefaultScoringModel
    ? likelihood != null && impact != null
      ? (calculateRating({ impact, likelihood })?.value ?? null)
      : null
    : rating;

  return getRiskScoreBadge(scoreRating, getByValue, score);
};
